import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button } from '@mui/material';
import { get } from 'lodash-es';
import { PERMISSIONS, Site, Zone, SiteAttendee } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import SiteStatusPanel from 'src/parts/overview/SiteStatusPanel';
import { Spinner } from '@repo/components';
import { getSite, getSiteAttendees, getZones } from 'src/api/admin/sites';

interface AttendanceSearch {
	search?: string;
	zoneIds?: string;
	date?: string;
	sort?: string;
}

export const Route = createFileRoute('/_admin/$siteId/overview/attendance')({
	validateSearch: (search: Record<string, unknown>): AttendanceSearch => ({
		search: search.search as string | undefined,
		zoneIds: search.zoneIds as string | undefined,
		date: search.date as string | undefined,
		sort: search.sort as string | undefined,
	}),
	component: AttendancePage,
});

// Environment variable would need to be configured differently in the manager app
const EXTRA_USER_PROPS_TO_SHOW_ARRAY: string[] = [];

interface SiteAttendeeWithSearch extends SiteAttendee {
	search?: string;
}

function createSearchableRow(row: SiteAttendee, extraUserPropsToShow: string[]) {
	const searchString = extraUserPropsToShow.map((prop) => get(row, `extraUserProps.${prop}`)).join(' ');
	return `${searchString} ${row.firstName || ''} ${row.lastName || ''} ${(row as any).phone || ''} ${(row as any).company || ''} ${row.zoneName || ''}`;
}

function AttendancePage() {
	const { siteId } = Route.useParams();
	const navigate = useNavigate();
	const router = useRouter();
	const { search, zoneIds, date, sort } = Route.useSearch();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config, loading: configLoading } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const extraUserPropsToShow = config?.extraUserPropsToShow || [];
	const { data: site, isLoading: siteLoading } = useQuery({
		queryKey: ['site', siteId],
		queryFn: () => getSite(siteId),
	});

	const { data: zonesResponse, isLoading: zonesLoading } = useQuery({
		queryKey: ['zones', siteId],
		queryFn: () => getZones(constructQuery({ selectedSiteId: siteId })),
	});

	const zones = (zonesResponse?.data || []).filter((zone: Zone) => zone.isVirtual !== true);
	const currentSiteZoneIds = zones.map((zone: Zone) => zone.id);
	const relevantZoneIds = zoneIds?.filter((id: string) => currentSiteZoneIds.includes(id));

	const { data: siteAttendeesResponse, isLoading: attendeesLoading } = useQuery({
		queryKey: ['site-attendees', siteId, relevantZoneIds?.join(','), date, sort],
		queryFn: () => getSiteAttendees(siteId, constructQuery({ zoneIds: relevantZoneIds?.join(','), date, sort })),
		enabled: !!site,
	});

	const isLoading = authLoading || configLoading || siteLoading || zonesLoading || attendeesLoading;

	if (isLoading || !site || !siteAttendeesResponse) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const handleRowClick = ({ row }: { row: SiteAttendeeWithSearch }) => {
		if (row.userId) {
			// Navigate to user detail page using navigate from @tanstack/react-router
			navigate({ to: '/users/$userId', params: { userId: row.userId } });
			return;
		}
	};

	const revalidateSiteOverview = () => {
		// Emergency-mode toggles update the Site doc, not just attendee data. Without invalidating the site query,
		// the UI can appear "stuck" until a full page refresh.
		queryClient.invalidateQueries({ queryKey: ['site', siteId] });
		queryClient.invalidateQueries({ queryKey: ['site-attendees', siteId] });
		// The emergency banner lives in the route layout, which is fed by TanStack Router loaders (not react-query).
		// Invalidating the router ensures `selectedSite.emergencyMode` updates immediately after toggling.
		router.invalidate();
	};

	const data: SiteAttendeeWithSearch[] = siteAttendeesResponse.data.map((siteAttendee) => ({
		...siteAttendee,
		search: createSearchableRow(siteAttendee, extraUserPropsToShow).trim().toLowerCase(),
	}));
	const totalCount = siteAttendeesResponse.count ?? siteAttendeesResponse.data.length;

	return (
		<div className="flex flex-col gap-4 p-4 pt-4">
			{date && (
				<Alert
					severity="info"
					variant="filled"
					action={
						<Button
							color="inherit"
							size="small"
							variant="outlined"
							onClick={() => updateQueryParams({ date: undefined })}
						>
							Clear Filter
						</Button>
					}
					sx={{ marginBottom: 2 }}
				>
					Filtered by date: {date}
				</Alert>
			)}
			<SiteStatusPanel
				search={search || ''}
				site={site}
				siteAttendees={data}
				count={totalCount}
				onRowClick={handleRowClick}
				onReload={revalidateSiteOverview}
				readOnly={!featureFlags.USES_RANDOM_CHECKS || !verifyPermission(PERMISSIONS.adminOverviewView)}
				showMusterZone={featureFlags.USES_EMERGENCY_MODULE}
				canEnableEmergencyMode={
					featureFlags.USES_EMERGENCY_MODULE && verifyPermission(PERMISSIONS.adminSecurityEmergencyMode)
				}
				useCheckinatwork={featureFlags.USE_CHECKINATWORK}
				useAntiPassback={!!featureFlags.USES_ANTI_PASSBACK}
				zones={zones}
				extraUserPropsToShow={extraUserPropsToShow}
			/>
		</div>
	);
}
